// src/Redux/thunks/cycleBotThunks.js
// ============================================================
// Thunks CycleBot + appels API intégrés (axios inline)
// cycleBotService.js supprimé — tout est ici
// ============================================================

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import Cookies from "js-cookie";

/* ══════════════════════════════════════════════════════════
   INSTANCE AXIOS (anciennement dans cycleBotService.js)
══════════════════════════════════════════════════════════ */
const trim = (s) => (s ? String(s).replace(/\/+$/, "") : "");
const baseURL = trim(import.meta.env.VITE_GATEWAY_URL) || "https://back.mediwave.tn/api";

const axiosCB = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

axiosCB.interceptors.request.use((config) => {
  const token = Cookies.get("accessToken") || "";
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

axiosCB.interceptors.response.use(
  (r) => r,
  (e) => Promise.reject({ status: e.response?.status, data: e.response?.data })
);

/* ══════════════════════════════════════════════════════════
   APPELS API (anciennement dans cycleBotService.js)
══════════════════════════════════════════════════════════ */
const api = {
  checkHealth:           ()                       => axiosCB.get("/cycle-bot/health").then(r => r.data),
  startChat:             ()                       => axiosCB.post("/cycle-bot/chat/start", {}).then(r => r.data),
  sendMessage:           (sid, msg)               => axiosCB.post("/cycle-bot/chat/message", { session_id: sid, message: msg }).then(r => r.data),
  logJournalEntry:       (payload)                => axiosCB.post("/cycle-bot/journal/log", payload).then(r => r.data),
  getJournalSummary:     ()                       => axiosCB.get("/cycle-bot/journal/summary").then(r => r.data),
  getJournalAnalysis:    ()                       => axiosCB.get("/cycle-bot/journal/analysis").then(r => r.data),
  getJournalEntries:     (days = 30)              => axiosCB.get("/cycle-bot/journal/entries", { params: { days } }).then(r => r.data),
  listFlows:             ()                       => axiosCB.get("/cycle-bot/flows").then(r => r.data),
  getFlowById:           (id)                     => axiosCB.get(`/cycle-bot/flows/${id}`).then(r => r.data),
  startFlow:             (flowId)                 => axiosCB.post("/cycle-bot/flows/start", { flow_id: flowId }).then(r => r.data),
  submitFlowAnswer:      (payload)                => axiosCB.post("/cycle-bot/flows/answer", payload).then(r => r.data),
  getFlowReport:         (sid)                    => axiosCB.get(`/cycle-bot/flows/report/${sid}`).then(r => r.data),
  deleteFlowSession:     (sid)                    => axiosCB.delete(`/cycle-bot/flows/${sid}`).then(() => null),
  getPremenstrualReport: ({ userId, nextJ1Date }) => axiosCB.post("/cycle-bot/journal/premenstrual-report", { user_id: userId, next_j1_date: nextJ1Date || null }).then(r => r.data),
  resetCycleJournal:     ({ userId, j1Date, report }) => axiosCB.post("/cycle-bot/journal/reset-cycle", { user_id: userId, j1_date: j1Date, report: report || null }).then(r => r.data),
  getJournalHistory:     ()                       => axiosCB.get("/cycle-bot/journal/history").then(r => r.data),
};

/* ── helper : normalise l'erreur axios ───────────────────── */
const toErr = (e, fallback = "Erreur inconnue") =>
  Array.isArray(e?.data?.message)
    ? e.data.message.join(", ")
    : e?.data?.message || e?.message || fallback;

/* ════════════════════════════════════════════════════════════
   HEALTH
════════════════════════════════════════════════════════════ */
export const fetchCycleBotHealth = createAsyncThunk(
  "cycleBot/fetchHealth",
  async (_, { rejectWithValue }) => {
    try { return await api.checkHealth(); }
    catch (e) { return rejectWithValue(toErr(e, "Service indisponible")); }
  }
);

/* ════════════════════════════════════════════════════════════
   CHAT
════════════════════════════════════════════════════════════ */
export const initCycleChat = createAsyncThunk(
  "cycleBot/initChat",
  async (_, { rejectWithValue }) => {
    try { return await api.startChat(); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible de démarrer le chat")); }
  }
);

export const sendCycleMessage = createAsyncThunk(
  "cycleBot/sendMessage",
  async ({ sessionId, message }, { rejectWithValue }) => {
    try { return await api.sendMessage(sessionId, message); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible d'envoyer le message")); }
  }
);

/* ════════════════════════════════════════════════════════════
   JOURNAL
════════════════════════════════════════════════════════════ */
export const logCycleEntry = createAsyncThunk(
  "cycleBot/logEntry",
  async (payload, { rejectWithValue }) => {
    try { return await api.logJournalEntry(payload); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible de sauvegarder l'entrée")); }
  }
);

export const fetchJournalSummary = createAsyncThunk(
  "cycleBot/journalSummary",
  async (_, { rejectWithValue }) => {
    try { return await api.getJournalSummary(); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible de charger le résumé")); }
  }
);

export const fetchJournalAnalysis = createAsyncThunk(
  "cycleBot/journalAnalysis",
  async (_, { rejectWithValue }) => {
    try { return await api.getJournalAnalysis(); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible de charger l'analyse")); }
  }
);

export const fetchJournalEntries = createAsyncThunk(
  "cycleBot/journalEntries",
  async (days = 30, { rejectWithValue }) => {
    try { return await api.getJournalEntries(days); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible de charger les entrées")); }
  }
);

/* ════════════════════════════════════════════════════════════
   FLOWS
════════════════════════════════════════════════════════════ */
export const fetchFlowsList = createAsyncThunk(
  "cycleBot/fetchFlows",
  async (_, { rejectWithValue }) => {
    try { return await api.listFlows(); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible de charger les flows")); }
  }
);

export const fetchFlowDetail = createAsyncThunk(
  "cycleBot/fetchFlowDetail",
  async (flowId, { rejectWithValue }) => {
    try { return await api.getFlowById(flowId); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible de charger le flow")); }
  }
);

export const beginCycleFlow = createAsyncThunk(
  "cycleBot/beginFlow",
  async (flowId, { rejectWithValue }) => {
    try { return await api.startFlow(flowId); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible de démarrer le flow")); }
  }
);

export const answerCycleFlow = createAsyncThunk(
  "cycleBot/answerFlow",
  async ({ flow_id, question_id, answer, session_id }, { rejectWithValue }) => {
    try { return await api.submitFlowAnswer({ flow_id, question_id, answer, session_id }); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible d'envoyer la réponse")); }
  }
);

export const fetchCycleFlowReport = createAsyncThunk(
  "cycleBot/fetchFlowReport",
  async (sessionId, { rejectWithValue }) => {
    try { return await api.getFlowReport(sessionId); }
    catch (e) { return rejectWithValue(toErr(e, "Impossible de charger le rapport")); }
  }
);

export const deleteCycleFlowSession = createAsyncThunk(
  "cycleBot/deleteFlowSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      await api.deleteFlowSession(sessionId);
      return sessionId;
    } catch (e) {
      return rejectWithValue(toErr(e, "Impossible de supprimer la session"));
    }
  }
);

/* ════════════════════════════════════════════════════════════
   RAPPORT PRÉ-MENSTRUEL
════════════════════════════════════════════════════════════ */
export const fetchPremenstrualReport = createAsyncThunk(
  "cycleBot/fetchPremenstrualReport",
  async ({ userId, nextJ1Date }, { rejectWithValue }) => {
    try { return await api.getPremenstrualReport({ userId, nextJ1Date }); }
    catch (err) { return rejectWithValue(err?.data?.detail || err?.message || "Erreur rapport"); }
  }
);

/* ════════════════════════════════════════════════════════════
   RESET CYCLE (J1 détecté — archive + vide journal)
════════════════════════════════════════════════════════════ */
export const resetCycleAtJ1 = createAsyncThunk(
  "cycleBot/resetCycleAtJ1",
  async ({ userId, j1Date, report }, { rejectWithValue, dispatch }) => {
    try {
      const result = await api.resetCycleJournal({ userId, j1Date, report });
      dispatch(fetchJournalEntries(30));
      dispatch(fetchJournalSummary());
      return result;
    } catch (err) {
      return rejectWithValue(err?.data?.detail || err?.message || "Erreur reset cycle");
    }
  }
);

export const fetchJournalHistory = createAsyncThunk(
  "cycleBot/fetchJournalHistory",
  async (_, { rejectWithValue }) => {
    try { return await api.getJournalHistory(); }
    catch (err) { return rejectWithValue(err?.message || "Erreur historique"); }
  }
);
